import { z } from "zod";
import { bannerKinds, billingEventKinds, billingStates, pauseAccessModes } from "../billing.ts";
import { isoDateTimeSchema, uuidSchema } from "./common.ts";

/**
 * Teamna subscription billing, the owner side (spec section 6.1): what Desk shows an owner or an
 * admin about the business's own Teamna plan. Not the money a business collects from its members
 * (`schemas/billing.ts`).
 */

export const billingStateSchema = z.enum(billingStates);
export const pauseAccessSchema = z.enum(pauseAccessModes);
export const bannerKindSchema = z.enum(bannerKinds);

/**
 * The short form on each `GET /v1/me/businesses` row, for the owner and an admin only: enough for the
 * Desk banner and the mobile banner without a call to Stripe. Null when billing is off on the
 * server, for a demo business, and for staff and members.
 */
export const businessBillingSummarySchema = z.object({
  access: z.enum(["full", "read_only"]),
  banner: bannerKindSchema.nullable(),
  /** A short machine reason (`grace`, `grace_over`, `ended`, ...), for logs and tests. */
  reason: z.string(),
  state: billingStateSchema.nullable(),
  trialEndsAt: isoDateTimeSchema.nullable(),
  graceEndsAt: isoDateTimeSchema.nullable(),
  currentPeriodEnd: isoDateTimeSchema.nullable(),
  pausedUntil: isoDateTimeSchema.nullable(),
});
export type BusinessBillingSummary = z.infer<typeof businessBillingSummarySchema>;

export const billingDiscountSchema = z.object({
  percentOff: z.number().nullable(),
  amountOffFils: z.number().int().nullable(),
  duration: z.enum(["once", "repeating", "forever"]),
  months: z.number().int().nullable(),
  /** When the discount stops. Null for `forever`, and for `once` until Stripe sets it. */
  endsAt: isoDateTimeSchema.nullable(),
});
export type BillingDiscountView = z.infer<typeof billingDiscountSchema>;

export const billingCardSchema = z.object({
  brand: z.string(),
  last4: z.string(),
  expMonth: z.number().int(),
  expYear: z.number().int(),
});

/** `GET /v1/owner/businesses/:id/billing`, the Billing page. */
export const ownerBillingSchema = businessBillingSummarySchema.extend({
  /** False when billing is off on this server (`BILLING_PROVIDER=off`). The page then says so. */
  enabled: z.boolean(),
  /**
   * Set while the business is in the Teamna pilot (Control Center): no trial, no plan, no charge.
   * Also set when billing is off on the server, so Control Center can mark pilots before the flip.
   */
  pilotSince: isoDateTimeSchema.nullable(),
  cancelAtPeriodEnd: z.boolean(),
  pauseAccess: pauseAccessSchema,
  priceFils: z.number().int(),
  discount: billingDiscountSchema.nullable(),
  creditFils: z.number().int(),
  card: billingCardSchema.nullable(),
  billingEmail: z.string().nullable(),
  /** The next charge from the provider's invoice preview. Null when none is planned or the preview failed. */
  nextCharge: z.object({ at: isoDateTimeSchema, amountFils: z.number().int() }).nullable(),
  /**
   * True when Desk should ask for a card now: there is no card and money is due, or the first charge
   * above zero is 7 days away or less (a 100% discount needs no card until it runs out).
   */
  needsCard: z.boolean(),
});
export type OwnerBilling = z.infer<typeof ownerBillingSchema>;

/** `POST .../billing/card-session` and `.../billing/portal-session`: a Stripe hosted page to open. */
export const billingSessionResponseSchema = z.object({ url: z.string().url() });
export type BillingSessionResponse = z.infer<typeof billingSessionResponseSchema>;

/** `PATCH .../billing/email`. Stripe sends the invoices and the receipts to this address. */
export const billingEmailRequestSchema = z.object({ email: z.string().trim().toLowerCase().email().max(254) });
export type BillingEmailRequest = z.infer<typeof billingEmailRequestSchema>;

// --- Control Center (spec section 6.2) ---------------------------------------------

export const billingEventKindSchema = z.enum(billingEventKinds);

/** One line of the billing feed. */
export const billingEventSchema = z.object({
  id: uuidSchema,
  businessId: uuidSchema,
  businessName: z.string(),
  kind: billingEventKindSchema,
  data: z.record(z.string(), z.unknown()),
  /** Set for a Control Center action: who did it, and why. */
  actorStaffEmail: z.string().nullable(),
  reason: z.string().nullable(),
  createdAt: isoDateTimeSchema,
});
export type BillingEvent = z.infer<typeof billingEventSchema>;

/** `GET /v1/admin/billing/events`, newest first. `nextBefore` pages to older lines. */
export const billingEventsResponseSchema = z.object({
  items: z.array(billingEventSchema),
  nextBefore: isoDateTimeSchema.nullable(),
});

/** The Billing column of the businesses list. Null for a demo, or a business with no subscription yet. */
export const adminBillingSummarySchema = businessBillingSummarySchema.extend({
  readOnlySince: isoDateTimeSchema.nullable(),
  priceFils: z.number().int(),
  /** What the business pays each month after its discount. 0 for a 100% discount. */
  monthlyFils: z.number().int(),
  hasCard: z.boolean(),
  hasDiscount: z.boolean(),
});
export type AdminBillingSummary = z.infer<typeof adminBillingSummarySchema>;

/** The numbers at the top of the businesses list. */
export const adminBillingOverviewSchema = z.object({
  /** `BILLING_PROVIDER=off`: no numbers to show. */
  enabled: z.boolean(),
  /** Active or cancelling, and paying more than zero. */
  paying: z.number().int(),
  /** The sum of `monthlyFils` of the paying businesses. */
  monthlyRevenueFils: z.number().int(),
  trialing: z.number().int(),
  trialEndingSoon: z.number().int(),
  pastDue: z.number().int(),
  readOnly: z.number().int(),
  /** Businesses that are past due, read-only, or both, each counted once. */
  pastDueOrReadOnly: z.number().int(),
  canceling: z.number().int(),
  ended: z.number().int(),
});
export type AdminBillingOverview = z.infer<typeof adminBillingOverviewSchema>;

export const billingInvoiceSchema = z.object({
  id: z.string(),
  number: z.string().nullable(),
  status: z.string(),
  totalFils: z.number().int(),
  amountPaidFils: z.number().int(),
  refundedFils: z.number().int(),
  createdAt: isoDateTimeSchema,
  hostedUrl: z.string().nullable(),
});
export type BillingInvoiceView = z.infer<typeof billingInvoiceSchema>;

/** `GET /v1/admin/businesses/:id/billing`, the Billing card. */
export const adminBillingSchema = ownerBillingSchema.extend({
  provider: z.string().nullable(),
  providerStatus: z.string().nullable(),
  priceLookupKey: z.string().nullable(),
  /** False when the business pays an older list price (grandfathered). */
  onCurrentPrice: z.boolean(),
  /** What the business pays each month after its discount. */
  monthlyFils: z.number().int(),
  accessOverrideUntil: isoDateTimeSchema.nullable(),
  readOnlySince: isoDateTimeSchema.nullable(),
  syncedAt: isoDateTimeSchema.nullable(),
  /** Links into the Stripe dashboard. Null with the fake provider. */
  dashboard: z.object({ customerUrl: z.string(), subscriptionUrl: z.string() }).nullable(),
  invoices: z.array(billingInvoiceSchema),
  events: z.array(billingEventSchema),
});
export type AdminBilling = z.infer<typeof adminBillingSchema>;

/** Every Control Center action needs a reason. It goes to `billing_events` and `audit_log`. */
const reasonSchema = z.string().trim().min(3, "Write why, in a few words.").max(500);

export const billingReasonRequestSchema = z.object({ reason: reasonSchema });
export type BillingReasonRequest = z.infer<typeof billingReasonRequestSchema>;

/** `trialEnd` null ends the trial now (the first charge happens at once). */
export const billingTrialEndRequestSchema = z.object({ trialEnd: isoDateTimeSchema.nullable(), reason: reasonSchema });
export type BillingTrialEndRequest = z.infer<typeof billingTrialEndRequestSchema>;

export const billingDiscountRequestSchema = z
  .object({
    percentOff: z.number().int().min(1).max(100).nullable(),
    amountOffFils: z.number().int().min(100).nullable(),
    duration: z.enum(["once", "repeating", "forever"]),
    months: z.number().int().min(1).max(36).nullable(),
    reason: reasonSchema,
  })
  .refine((value) => (value.percentOff === null) !== (value.amountOffFils === null), {
    message: "Give a percent or an amount, not both.",
    path: ["percentOff"],
  })
  .refine((value) => (value.duration === "repeating") === (value.months !== null), {
    message: "Give the number of months for a discount that repeats, and only then.",
    path: ["months"],
  });
export type BillingDiscountRequest = z.infer<typeof billingDiscountRequestSchema>;

/** `days` 0 ends the override now. */
export const billingKeepAccessRequestSchema = z.object({ days: z.number().int().min(0).max(90), reason: reasonSchema });
export type BillingKeepAccessRequest = z.infer<typeof billingKeepAccessRequestSchema>;

/** `amountFils` null refunds what is left on the invoice. */
export const billingRefundRequestSchema = z.object({
  invoiceId: z.string().min(1).max(255),
  amountFils: z.number().int().min(1).nullable(),
  reason: reasonSchema,
});
export type BillingRefundRequest = z.infer<typeof billingRefundRequestSchema>;

export const billingPauseRequestSchema = z.object({
  until: isoDateTimeSchema,
  access: pauseAccessSchema,
  reason: reasonSchema,
});
export type BillingPauseRequest = z.infer<typeof billingPauseRequestSchema>;

/** `pilot` true makes the business a pilot (no trial, no charge); false ends it and starts the 7-day trial. */
export const billingPilotRequestSchema = z.object({ pilot: z.boolean(), reason: reasonSchema });
export type BillingPilotRequest = z.infer<typeof billingPilotRequestSchema>;

/** At most AED 10,000 in one step. */
export const billingCreditRequestSchema = z.object({ amountFils: z.number().int().min(100).max(1_000_000), reason: reasonSchema });
export type BillingCreditRequest = z.infer<typeof billingCreditRequestSchema>;
